import axios from "axios";
import { getStorage, clearStorage } from "../../utils/storage";
import { APP_CONFIG } from "./apiConfig";
import { methodType } from "./apiEndPoints";

axios.interceptors.response.use(
  (response) => {
    // Add null check for response object
    if (!response) {
      return Promise.reject(new Error('Invalid response object'));
    }
    const { data } = response;
    if (response.config?.responseType === "blob") {
      return data;
    }

    // 204 No Content and other empty-body 2xx responses are valid successes
    // (e.g. DELETE endpoints). Don't treat them as errors.
    if (data === "" || data === null || data === undefined) {
      return { success: true, status: response.status };
    }

    return data;
  },
  async (error) => {
    // Comprehensive null/undefined checks for error object
    if (!error) {
      return Promise.reject(new Error('Unknown error occurred'));
    }

    // Check if error has a response object
    if (!error.response) {
      // Network errors, timeouts, etc. don't have a response object
      return Promise.reject(error);
    }

    // Safely extract response properties with null checks
    const response = error.response;
    const data = response?.data || null;
    const config = response?.config || error?.config || null;
    const status = response?.status || null;

    // Handle 401 Unauthorized errors (token expired/invalid)
    if (status === 401 && config && config.url && !config.url.includes("login")) {
      clearStorage();
      return Promise.reject(data || { message: 'Unauthorized access' });
    }

    // Return error data if available, otherwise return the full error
    if (data) {
      return Promise.reject(data);
    } else {
      // Fallback: return a structured error object
      return Promise.reject({
        message: error?.message || 'An error occurred',
        status: status,
        ...error
      });
    }
  }
);

export const performRequest = async (
  method,
  url,
  data,
  login = true,
  formData = false,
  extraConfig = {}
) => {
  url = url.replaceAll("#", "%23");
  const config = {
    method,
    url,
    baseURL: APP_CONFIG.API_BASE_URL,
    timeout: 30000, // 30 seconds timeout
  };

  if (
    method === methodType.PUT ||
    method === methodType.PATCH ||
    method === methodType.POST ||
    method === methodType.DELETE
  ) {
    config.data = data;
  }

  if (method === methodType.GET) {
    config.params = data;
  }
  if (formData) {
    config.headers = {
      "Content-Type": "multipart/form-data",
    };
  } else {
    config.headers = {
      "Content-Type": "application/json; charset=utf-8",
    };
  }

  if (login) {
    let token = getStorage("access_token") || null;
    config.headers.Authorization = `Bearer ${token}`;
  }

  return axios.request({
    ...config,
    ...extraConfig,
  });
};

/**
 * Standardized API request wrapper with consistent error handling
 * @param {string} method - HTTP method (GET, POST, PUT, PATCH, DELETE)
 * @param {string} endpoint - API endpoint path (without base URL)
 * @param {object} data - Request data/params
 * @param {boolean} requiresAuth - Whether the request requires authentication
 * @param {boolean} isFormData - Whether the request is form data
 * @returns {Promise<any>} Returns the API response directly on success, or {success: false, error: any} on error
 */
export const apiRequest = async (
  method,
  endpoint,
  data = {},
  requiresAuth = true,
  isFormData = false
) => {
  try {
    // Remove base URL if it's already included in the endpoint
    const cleanEndpoint = endpoint.startsWith(APP_CONFIG.API_BASE_URL)
      ? endpoint.replace(APP_CONFIG.API_BASE_URL, '')
      : endpoint;

    const response = await performRequest(
      method,
      cleanEndpoint,
      data,
      requiresAuth,
      isFormData
    );

    // Return response directly to maintain compatibility with existing code
    // The response may already have a 'success' property from the API
    return response;
  } catch (error) {
    // Standardized error response format for consistent error handling

    const extractedError =
      error?.response?.data ||
      error?.data ||
      (Array.isArray(error?.error)
        ? error.error[0]
        : error?.error?.message || error?.error);

    const errorResponse = {
      success: false,
      error: extractedError || {
        message: error?.message || 'An unexpected error occurred',
      },
    };

    // Include additional error details if available
    if (error?.response?.status) {
      errorResponse.error.status = error.response.status;
    }

    return errorResponse;
  }
};
